import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { CommunicationService, MessageVM, TenantService, } from "@propertelligent/client-api";
import ChatBox from "./ChatBox";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import { UserLoginData } from "../../common/charts/Enums";
import { formatChatDate } from "../../../utils/DateHelper";
import SimpleSpinner from "../../loader";
import ChatListingTopHeader from "../../../newComponents/listings/top-listing-headers/chat-listing-top-header";
import UtilHelper from "../../../utils/UtilHelper";
import { Background } from "../../GlobalCss/Background";
import { TextCss } from "../../GlobalCss/TextCss";
import { Colors } from "../../GlobalCss/Colors";
import NameAvatarComponent from "../../../newComponents/NameAvatar/NameAvatar";
import { usePermissions } from "../../../hooks/usePermissions";
import { escapeDynamicLinqStringLiteral } from "../../../utils/dynamicLinq";

const ChatView = () => {
  const [searchValues, setSearchValues] = useState("");
  const [entityId, setEntityId] = useState();
  const [entityTypeId, setEntityTypeId] = useState();
  const [chatname, setChatName] = useState("");
  const [showSection, setShowSection] = useState("");
  const [messageChannel, setMessageChannel] = useState([]);
  const [loading, setLoading] = useState(true);
  // Distinguishes the very first load (no data has ever been fetched yet -- the only time the
  // whole module, including the header/search bar, should be replaced by a full-page spinner)
  // from every later load. Without this, searching to 0 results and then clearing/changing the
  // search re-triggered the exact same full-page spinner (losing the header and search box
  // entirely) just because messageChannel still happened to be empty at that moment.
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedOption, setSelectedOption] = useState<String>("All Messages");
  const [showMessage, setShowMessage] = useState(false);
  const [tenantList, settenantList] = useState([]);
  const [count, setCount] = useState<number>()
  const inputRef = useRef(null)
  const [debouncedSearchValues] = useDebounce(searchValues, 500);

  const [messageData, setmessageData] = useState<MessageVM>({
    messageData: "",
    entityTypeId: entityTypeId,
    entityId: entityId,
  });

  const { canWrite } = usePermissions();
  const hasWritePermission = canWrite("Message");

  let api = selectedOption == "All Messages" ? CommunicationService.getApiMessageOrganizationlistMessagechannels :
    CommunicationService.getApiMessageOrganizationlistMessagechannelsUnread
  // showLoading=false for the background refresh ChatBox fires after a successful send (it only
  // needs to update the list's last-message preview/unread count/order in place) -- previously
  // this always set loading=true, which swaps the whole tenant list out for a full-screen spinner
  // on every single message sent, not just on an actual explicit list reload.
  const fetchMessageChannel = useCallback((showLoading = true) => {
    if (showLoading) setLoading(true);
    const
      searchQuery =
        typeof debouncedSearchValues === "string" ? debouncedSearchValues.toLowerCase() : "";

    api(
      parseInt(sessionStorage.getItem(UserLoginData.organization_id)),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      `(usr.FirstName + " " + usr.LastName).ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchValues.toLowerCase().trim())}")`
    ).then((res) => {
      setCount(res?.recordCount)
      setMessageChannel(res?.success);
      if (showLoading) setLoading(false);
      setIsInitialLoad(false);
    })
      .catch((error) => {
        console.error(error);
        if (showLoading) setLoading(false);
        setIsInitialLoad(false);
      });
  }, [selectedOption, showSection, debouncedSearchValues]);

  useEffect(() => {
    fetchMessageChannel();
  }, [debouncedSearchValues, selectedOption]);

  // Populates the "Search Tenant" picker's full tenant list once on mount -- that list is
  // filtered entirely client-side (see ChatBox.tsx's checkMatch()), so this never needs to
  // re-fetch on keystroke.
  const fetchList = () => {
    TenantService.getApiTenantOrganizationlookup(
      parseInt(sessionStorage.getItem(UserLoginData.organization_id)),
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    ).then((res) => {
      settenantList(res.success);
    }).catch((error) => { });
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullName = sessionStorage.getItem(UserLoginData.user_name) || "";
  const nameParts = fullName.split(/\s+/).filter(Boolean);

  const firstName = nameParts[0] || "";
  const lastName = nameParts[1] || "";

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  const handleSelectChange = useCallback((value: String) => setSelectedOption(value), []);
  const handleClickChange = useCallback((item) => {
    setEntityId(item.entityId);
    setEntityTypeId(item?.entityTypeId);
    setChatName(item?.name);
    setShowSection("chat");


    setMessageChannel((prev) =>
      prev.map((channel) =>
        channel.entityId === item.entityId
          ? { ...channel, unreadMessagesCount: 0 }
          : channel
      )
    );
  }, []);

  useEffect(() => {
    if (!loading && showSection === "") {
      if (messageChannel?.length > 0) {
        handleClickChange(messageChannel[0]);
      } else {
        setEntityId(null);
        setShowSection("selectChat");
      }
    }
  }, [loading, messageChannel, showSection, handleClickChange]);

  return (
    <>
      {isInitialLoad && loading ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 w-100" style={{ minHeight: "80vh" }}>
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <div className="chatMainContainer">
          <ChatListingTopHeader
            PageHeading="Messages"
            ButtonName={hasWritePermission ? "New Message" : undefined}
            handleSort={() => { }}
            currentSortDirection={undefined}
            Count={count}
            handleSelectChange={handleSelectChange}
            searchTerm={searchValues}
            setSearchTerm={setSearchValues}
            filterOptions={undefined}
            setActiveTab={undefined}
            activeTab={undefined}
            aggregateValuesFilters={{
              "Unread Only": 0
            }}
            onClick={() => { setEntityId(null); setShowSection("selectChat"); handleClick() }}
          />
          <div className="chatSubContainer  ">
            <div className="chatLeftSection">
              {loading ? (
                <SimpleSpinner />
              ) : (
                <div className="chatItemContaner " >
                  {messageChannel?.length == 0 ? (
                    <EmptyState
                      className="chat-list-empty"
                      heading="No Messages Found"
                      bodytext=""
                      isButton={false}
                      icon={GetImages.EmptyEmail}
                      imgBackgroundClass={Background.LightBlue.Round.round80}
                      isBorder={false}
                    />
                  ) : (
                    messageChannel?.map((item, i) => {
                      const name = item?.name?.split(/\s+/).filter(Boolean);
                      return (
                        <div
                          className="chatItem "
                          style={{
                            background:
                              item.entityId === entityId
                                ? "var(--Selected-Bg)"
                                : "",
                          }}
                          key={i}
                          onClick={() => handleClickChange(item)} >
                          {/* <div className="userIcon">
                          <NameAvatarComponent
                            firstName={name[0]}
                            lastName={name[1]}
                            heightWidth={40}
                          />
                        </div> */}
                          <NameAvatarComponent
                            firstName={name[0]}
                            lastName={name[1]}
                            heightWidth={40}
                            shape="square"
                            className={TextCss.heading_700_16}
                            color={Colors.blue.regular}
                            backgroundColor={Colors.blue.lighter}
                          />

                          <div className="chatDetailsView">
                            <div className="flexBetweenAlignCenter">
                              <p className={TextCss.black.normal_text_700_14}> {item?.name} </p>
                              <p className="sub_text  grey_subtext1"> {formatChatDate(item?.lastMessageDateTime)} </p>
                            </div>
                            <div className="message_count sub_text d-flex flexBetweenAlignCenter">
                              <p className="sub_text  grey_subtext1" id="message-data" > {item?.lastMessageData} </p>
                              {item.unreadMessagesCount > 0 ? (
                                <div className="meesageCount">
                                  <p className="small_text white"> {item.unreadMessagesCount} </p>
                                </div>
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            {/* >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>> */}
            <div className="chatRightSection">

              {showSection == "" ? <div className="chatboxContainer">
                <EmptyState
                  icon={GetImages.EmptyEmail}
                  heading={"Select An Item To Read"}
                  imgBackgroundClass={Background.LightBlue.Round.round80}
                  isButton={false}
                  isBorder={false}
                /> </div> :
                <ChatBox
                  tenantId={undefined}
                  setShowMessage={setShowMessage}
                  entityId={entityId}
                  setEntityId={setEntityId}
                  entityTypeId={entityTypeId}
                  chatname={chatname}
                  setChatName={setChatName}
                  setEntityTypeId={setEntityTypeId}
                  fetchData={fetchMessageChannel}
                  messageData={messageData}
                  setmessageData={setmessageData}
                  tenantList={tenantList}
                  firstName={firstName}
                  lastName={lastName}
                  inputRef={inputRef}
                  hasWritePermission={hasWritePermission}
                  fetchList={fetchList} setShowSection={setShowSection} messageChannel={messageChannel} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatView;
